import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Ticket, Plus, Loader2, Save, Trash2, Users as UsersIcon, Search, X, Power, ChevronRight, Pencil } from "lucide-react";

const EMPTY_FORM = {
  code: "", name: "", discount_percent: 10, max_rental_days: 0,
  target_type: "all", target_user_id: "", target_user_email: "",
  usage_limit: 0, active: true,
};

export default function AdminPromoCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode; id = edit mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [usagesModal, setUsagesModal] = useState(null); // {id, code, usages: []}

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setCodes(data || []);
    } catch (e) {
      toast.error("Impossible de charger les codes");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchCodes(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      code: c.code,
      name: c.name || "",
      discount_percent: c.discount_percent,
      max_rental_days: c.max_rental_days || 0,
      target_type: c.target_type || "all",
      target_user_id: c.target_user_id || "",
      target_user_email: c.target_user_email || "",
      usage_limit: c.usage_limit || 0,
      active: !!c.active,
    });
    setEditingId(c.id);
    setShowForm(true);
  };
  const submitForm = async () => {
    if (!form.code.trim()) return toast.error("Renseignez un code");
    if (form.discount_percent <= 0 || form.discount_percent > 100) return toast.error("Pourcentage entre 1 et 100");
    if (form.target_type === "specific_user" && !form.target_user_id) return toast.error("Choisissez un client bénéficiaire");
    setSaving(true);
    try {
      if (editingId) {
        // Edit mode: code itself is immutable (used in bookings history) — only editable fields sent.
        await api.put(`/admin/promo-codes/${editingId}`, {
          name: form.name.trim() || form.code.trim().toUpperCase(),
          discount_percent: Number(form.discount_percent),
          max_rental_days: Number(form.max_rental_days || 0),
          target_type: form.target_type,
          target_user_id: form.target_type === "specific_user" ? form.target_user_id : "",
          usage_limit: Number(form.usage_limit || 0),
          active: !!form.active,
        });
        toast.success("Code modifié");
      } else {
        await api.post("/admin/promo-codes", {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim() || form.code.trim().toUpperCase(),
          discount_percent: Number(form.discount_percent),
          max_rental_days: Number(form.max_rental_days || 0),
          target_type: form.target_type,
          target_user_id: form.target_type === "specific_user" ? form.target_user_id : "",
          usage_limit: Number(form.usage_limit || 0),
          active: !!form.active,
        });
        toast.success("Code créé");
      }
      setShowForm(false);
      setEditingId(null);
      fetchCodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const toggleActive = async (c, next) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, { active: next });
      fetchCodes();
    } catch (e) { toast.error("Erreur"); }
  };
  const softDelete = async (c) => {
    const msg = c.times_used > 0
      ? `Supprimer définitivement le code ${c.code} ?\n\n⚠️ Cela effacera aussi ${c.times_used} utilisation(s) dans l'historique.`
      : `Supprimer définitivement le code ${c.code} ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Code supprimé");
      fetchCodes();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openUsages = async (c) => {
    try {
      const { data } = await api.get(`/admin/promo-codes/${c.id}/usages`);
      setUsagesModal({ id: c.id, code: c.code, usages: data || [] });
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-promo-codes-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Codes Promo</h2>
        </div>
        <Button onClick={openCreate} className="bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="promo-code-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau code
        </Button>
      </div>

      <p className="text-xs text-[#64748B]">
        Créez des codes de réduction ponctuels. Un code entré par le client au moment de la commande <strong>remplace</strong> la promo bienvenue -15%. Chaque client ne peut utiliser un même code qu&apos;une seule fois.
      </p>

      {loading ? (
        <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" /></div>
      ) : codes.length === 0 ? (
        <div className="bg-white border border-dashed border-[#CBD5E1] rounded-xl p-8 text-center text-sm text-[#64748B]" data-testid="promo-codes-empty">
          Aucun code créé. Cliquez sur <strong>Nouveau code</strong> pour commencer.
        </div>
      ) : (
        <div className="bg-white border border-[#CBD5E1] rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="promo-codes-table">
            <thead className="bg-[#F1F5F9] text-[#0A2540]">
              <tr>
                <th className="p-3 text-left">Code</th>
                <th className="p-3 text-left">Nom</th>
                <th className="p-3 text-center">%</th>
                <th className="p-3 text-center">Jours max</th>
                <th className="p-3 text-left">Cible</th>
                <th className="p-3 text-center">Utilisations</th>
                <th className="p-3 text-center">Actif</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} className="border-t border-[#F1F5F9] hover:bg-[#FEFCE8]/40" data-testid={`promo-code-row-${c.code}`}>
                  <td className="p-3 font-mono font-bold text-[#0A2540]">{c.code}</td>
                  <td className="p-3">{c.name}</td>
                  <td className="p-3 text-center text-[#FACC15] font-bold">-{c.discount_percent}%</td>
                  <td className="p-3 text-center text-[#64748B]">{c.max_rental_days > 0 ? `${c.max_rental_days} j` : "—"}</td>
                  <td className="p-3 text-xs">
                    {c.target_type === "specific_user"
                      ? <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" />{c.target_user_email || c.target_user_name || "(client)"}</span>
                      : <span className="text-[#64748B]">Tous les clients</span>}
                  </td>
                  <td className="p-3 text-center">
                    <button onClick={() => openUsages(c)} className="underline hover:text-[#0A2540]" data-testid={`promo-code-usages-${c.code}`}>
                      {c.times_used || 0}{c.usage_limit > 0 ? ` / ${c.usage_limit}` : ""}
                    </button>
                  </td>
                  <td className="p-3 text-center">
                    <Switch checked={!!c.active} onCheckedChange={(v) => toggleActive(c, v)} data-testid={`promo-code-active-${c.code}`} />
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} className="text-[#0A2540] hover:opacity-70 p-1.5 rounded hover:bg-[#F1F5F9]" title="Modifier" data-testid={`promo-code-edit-${c.code}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => softDelete(c)} className="text-[#EF4444] hover:opacity-70 p-1.5 rounded hover:bg-[#FEE2E2]" title="Supprimer" data-testid={`promo-code-delete-${c.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit form dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-lg" data-testid="promo-code-form-dialog">
          <DialogTitle className="text-lg font-bold text-[#0A2540]">{editingId ? "Modifier le code promo" : "Nouveau code promo"}</DialogTitle>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Code (client tapera ceci){editingId && <span className="text-[#64748B] font-normal ml-1">— non modifiable</span>}</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })}
                placeholder="ex. SUMMER20" className="uppercase font-mono" disabled={!!editingId} data-testid="promo-code-input-code" />
            </div>
            <div>
              <Label className="text-xs">Nom / libellé interne</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex. Promo été 2026" data-testid="promo-code-input-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">% Réduction (1-100)</Label>
                <Input type="number" min={1} max={100} value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="promo-code-input-percent" />
              </div>
              <div>
                <Label className="text-xs">Jours max de location (0 = illimité)</Label>
                <Input type="number" min={0} value={form.max_rental_days}
                  onChange={(e) => setForm({ ...form, max_rental_days: e.target.value })} data-testid="promo-code-input-maxdays" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Client bénéficiaire</Label>
              <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v, target_user_id: "", target_user_email: "" })}>
                <SelectTrigger data-testid="promo-code-input-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les clients</SelectItem>
                  <SelectItem value="specific_user">Un client précis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.target_type === "specific_user" && (
              <UserPickerInline value={form.target_user_id} label={form.target_user_email}
                onChange={(u) => setForm({ ...form, target_user_id: u.id, target_user_email: u.email })} />
            )}
            <div>
              <Label className="text-xs">Limite globale d&apos;utilisations (0 = illimité)</Label>
              <Input type="number" min={0} value={form.usage_limit}
                onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} data-testid="promo-code-input-limit" />
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC]">
              <Power className={`w-4 h-4 ${form.active ? "text-[#10B981]" : "text-[#EF4444]"}`} />
              <span className="text-sm flex-1">Actif dès la création</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-code-input-active" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} className="flex-1" data-testid="promo-code-cancel-btn">Annuler</Button>
              <Button onClick={submitForm} disabled={saving} className="flex-1 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-bold" data-testid="promo-code-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                {editingId ? "Enregistrer" : "Créer le code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Usages modal */}
      <Dialog open={!!usagesModal} onOpenChange={(o) => !o && setUsagesModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogTitle className="text-lg font-bold text-[#0A2540]">Utilisations · {usagesModal?.code}</DialogTitle>
          {usagesModal && usagesModal.usages.length === 0 ? (
            <p className="text-sm text-[#64748B] py-6 text-center">Aucune utilisation pour le moment.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto mt-2">
              <table className="w-full text-sm">
                <thead className="bg-[#F1F5F9]"><tr><th className="p-2 text-left">Client</th><th className="p-2 text-right">Réduction</th><th className="p-2 text-right">Date</th></tr></thead>
                <tbody>
                  {usagesModal?.usages.map(u => (
                    <tr key={u.id} className="border-t border-[#F1F5F9]">
                      <td className="p-2">{u.user_email || u.user_name || u.user_id}</td>
                      <td className="p-2 text-right text-[#FACC15] font-bold">-{(u.discount_amount || 0).toLocaleString("fr-FR")} F</td>
                      <td className="p-2 text-right text-xs text-[#64748B]">{new Date(u.used_at).toLocaleString("fr-FR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserPickerInline({ value, label, onChange }) {
  const [q, setQ] = useState(label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q || q.length < 2 || q === label) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/promo-codes/search-users?q=${encodeURIComponent(q)}`);
        if (!cancelled) { setResults(data || []); setOpen(true); }
      } catch { /* noop */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, label]);

  const pick = (u) => {
    setQ(u.email || u.phone || u.name);
    setResults([]); setOpen(false);
    onChange(u);
  };

  return (
    <div className="relative">
      <Label className="text-xs">Rechercher le client (email / téléphone / nom)</Label>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#64748B]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)}
          className="pl-8" placeholder="ex. jean@…" data-testid="promo-code-user-search" />
        {value && (
          <button type="button" onClick={() => { setQ(""); onChange({ id: "", email: "" }); }}
            className="absolute right-2 top-2.5 text-[#64748B] hover:text-[#EF4444]">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#CBD5E1] rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="promo-code-user-results">
          {results.map(u => (
            <button key={u.id} type="button" onClick={() => pick(u)}
              className="w-full text-left px-3 py-2 hover:bg-[#FEFCE8] text-sm flex items-center justify-between gap-2">
              <span>
                <strong>{u.name}</strong>
                <span className="block text-xs text-[#64748B]">{u.email} · {u.phone}</span>
              </span>
              <ChevronRight className="w-4 h-4 text-[#64748B]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
